<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-gray-50 text-gray-900">

    <!-- Studio Header -->
    <header class="h-14 bg-white/85 backdrop-blur-md border-b border-gray-200/60 flex items-center justify-between px-4 shrink-0 z-50">
      <div class="flex items-center gap-3">
        <!-- Desktop sidebar toggle -->
        <el-button
          v-if="!isMobile"
          circle text
          @click="toggleDataPanel"
          :type="showDataPanel ? 'primary' : ''"
          :class="{ '!bg-indigo-50': showDataPanel }"
        >
          <el-icon :size="18"><Menu /></el-icon>
        </el-button>

        <div class="flex items-center gap-3" :class="{ 'ml-2 border-l border-gray-200/60 pl-4 h-7': !isMobile }">
           <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold shadow-sm shadow-indigo-500/25">W</div>
           <h1 class="font-bold text-lg tracking-tight hidden md:inline select-none m-0 leading-normal">WordCloud<span class="text-indigo-600">Studio</span></h1>
        </div>
      </div>

      <!-- Center nav — crawlable internal links for SEO -->
      <nav class="hidden lg:flex items-center gap-4 text-xs text-gray-400">
        <a href="/price/" class="hover:text-indigo-600 transition-colors">Pricing</a>
        <a href="/mask/" class="hover:text-indigo-600 transition-colors">Shapes</a>
        <a href="/about/" class="hover:text-indigo-600 transition-colors">About</a>
        <a href="/blog/" class="hover:text-indigo-600 transition-colors">Blog</a>
      </nav>

      <div class="flex items-center gap-2">
         <el-button circle text @click="serviceVisible = true" :title="$t('Customer Service')">
           <el-icon :size="18"><Headset /></el-icon>
         </el-button>
         <el-button circle text @click="showOnboarding = true" :title="$t('Help')">
           <el-icon :size="18"><QuestionFilled /></el-icon>
         </el-button>
         <!-- Desktop sidebar toggle -->
         <el-button
           v-if="!isMobile"
           circle text
           @click="toggleOptionsPanel"
           :type="showOptionsPanel ? 'primary' : ''"
           :class="{ '!bg-indigo-50': showOptionsPanel }"
         >
           <el-icon :size="18"><Setting /></el-icon>
         </el-button>
         <!-- Mobile language switch -->
         <el-select
           v-if="isMobile"
           :model-value="$i18n.locale"
           @change="(val) => $i18n.locale = val"
           size="small"
           class="!w-20"
           popper-class="mobile-lang-popper"
         >
           <el-option value="en" label="EN" />
           <el-option value="zh-CN" label="中文" />
           <el-option value="de" label="DE" />
           <el-option value="es" label="ES" />
           <el-option value="fr" label="FR" />
           <el-option value="it" label="IT" />
           <el-option value="ja" label="日本語" />
           <el-option value="ko" label="한국어" />
           <el-option value="pt" label="PT" />
         </el-select>
      </div>
    </header>

    <!-- Main Workspace -->
    <div class="flex-1 flex overflow-hidden relative">

      <!-- Left Panel (Desktop Only) -->
      <transition name="panel-slide-left">
        <aside
          v-if="!isMobile"
          v-show="showDataPanel"
          class="bg-white border-r border-gray-200 flex flex-col z-40 w-80 relative transition-all duration-300"
        >
           <LeftSidebar
            :isMobile="false"
            :isSubscribed="isSubscribed"
            v-model:textToAnalyze="textToAnalyze"
            :processedChartData="processedChartData"
            :loading="loading"
            :tableKey="tableKey"
            @analyze-text="analyzeText"
            @add-data="addData"
            @delete-data="deleteData"
            @update-data="updateData"
            @change-lang="changeLang"
            @price-click="priceVisible = true"
            @open-service="serviceVisible = true"
            @clear-data="chartData = []"
           />
        </aside>
      </transition>

      <!-- Center Canvas (Artboard) -->
      <main class="flex-1 bg-gray-100 bg-dot-pattern relative overflow-hidden flex flex-col items-center justify-center">

        <!-- Zoom/Toolbar (desktop) -->
        <div class="absolute top-4 z-20 toolbar hidden md:flex">
           <el-button circle text size="small" @click="zoomOut" :disabled="zoomLevel <= 0.1"><el-icon><Remove /></el-icon></el-button>
           <span class="text-xs font-mono w-12 text-center text-gray-500 select-none">{{ Math.round(zoomLevel * 100) }}%</span>
           <el-button circle text size="small" @click="zoomIn" :disabled="zoomLevel >= 3"><el-icon><Plus /></el-icon></el-button>
           <div class="w-px h-4 bg-gray-200 mx-0.5"></div>
           <el-button circle text size="small" @click="resetZoom" :title="$t('Reset View')"><el-icon><Refresh /></el-icon></el-button>
        </div>

        <!-- The Artboard -->
        <div
          class="relative transition-transform duration-200 ease-out shadow-xl bg-white ring-1 ring-black/5 rounded-lg"
          :style="artboardStyle"
        >
           <!-- Loading Overlay -->
           <div v-if="loading" class="absolute inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center">
              <div class="relative w-full h-full max-w-[80%] max-h-[80%] flex items-center justify-center">
                 <!-- Word cloud skeleton: simulated word shapes -->
                 <div class="skeleton absolute w-32 h-6 top-[15%] left-[20%] rounded-full" style="animation-delay: 0s"></div>
                 <div class="skeleton absolute w-24 h-8 top-[25%] left-[55%] rounded-full" style="animation-delay: 0.1s"></div>
                 <div class="skeleton absolute w-28 h-5 top-[40%] left-[40%] rounded-full" style="animation-delay: 0.2s"></div>
                 <div class="skeleton absolute w-20 h-7 top-[35%] left-[10%] rounded-full" style="animation-delay: 0.15s"></div>
                 <div class="skeleton absolute w-36 h-5 top-[55%] left-[30%] rounded-full" style="animation-delay: 0.25s"></div>
                 <div class="skeleton absolute w-16 h-5 top-[50%] left-[65%] rounded-full" style="animation-delay: 0.3s"></div>
                 <div class="skeleton absolute w-22 h-6 top-[70%] left-[50%] rounded-full" style="animation-delay: 0.35s"></div>
                 <div class="skeleton absolute w-18 h-5 top-[68%] left-[15%] rounded-full" style="animation-delay: 0.4s"></div>
                 <span class="absolute bottom-4 text-xs font-semibold text-indigo-500/80">Generating...</span>
              </div>
           </div>

           <WordCloudChart
             ref="wordCloud"
             :isSubscribed="isSubscribed"
             :isMobile="isMobile"
             :chartData="chartData"
             :maskSrc="currentMaskSrc"
             :font="selectedFont"
             :backgroundColor="backgroundColor"
             :width="imageWidth"
             :height="imageHeight"
             :minSize="minSize"
             :maxSize="maxSize"
             :carouselImages="imageUrls"
             :bgImageBase64="bgImageBase64"
             :bgImageOpacity="bgOpacity"
             :bgImageBlur="bgBlur"
             :bgImageFitMode="bgFitMode"
             :colorPalette="currentPalette"
           />
        </div>

      </main>

      <!-- Right Panel (Desktop Only) -->
      <transition name="panel-slide-right">
        <aside
          v-if="!isMobile"
          v-show="showOptionsPanel"
          class="bg-white border-l border-gray-200 flex flex-col z-40 w-80 relative transition-all duration-300"
        >
          <RightSidebar
            :isMobile="false"
            v-model:selectedFont="selectedFont"
            :fontOptions="fontOptions"
            v-model:backgroundColor="backgroundColor"
            :predefineColors="predefineColors"
            :selectedSize="selectedSize"
            :defaultSizes="defaultSizes"
            v-model:imageWidth="imageWidth"
            v-model:imageHeight="imageHeight"
            :selectedPalette="selectedPalette"
            :bgImage="bgImageBase64"
            :bgOpacity="bgOpacity"
            :bgBlur="bgBlur"
            :bgFitMode="bgFitMode"
            :isRecording="isRecording"
            :isGeneratingGif="isGeneratingGif"
            @update-chart="handleUpdateChart"
            @export-layout="exportLayout"
            @open-mask-dialog="maskVisible = true"
            @mask-file-selected="handleMaskFileSelect"
            @update-image-size="updateImageSize"
            @update:selectedPalette="selectedPalette = $event"
            @open-drawing-board="drawingBoardVisible = true"
            @bg-file-selected="handleBgFileSelect"
            @update:bgOpacity="bgOpacity = $event"
            @update:bgBlur="bgBlur = $event"
            @update:bgFitMode="bgFitMode = $event"
            @clear-bg-image="clearBgImage"
            @export-video="handleExportVideo"
            @export-gif="handleExportGif"
          />
        </aside>
      </transition>

    </div>

    <!-- Mobile: iOS-style Config Bar + Action Bar -->
    <template v-if="isMobile">
        <!-- Mobile Zoom Bar -->
        <div class="mobile-zoom-bar shrink-0 border-t border-gray-200/60 bg-white/90 backdrop-blur-md flex items-center justify-center gap-1 px-2 py-1.5">
           <el-button circle text size="small" @click="zoomOut" :disabled="zoomLevel <= 0.1"><el-icon><Remove /></el-icon></el-button>
           <span class="text-xs font-mono w-12 text-center text-gray-500 select-none">{{ Math.round(zoomLevel * 100) }}%</span>
           <el-button circle text size="small" @click="zoomIn" :disabled="zoomLevel >= 3"><el-icon><Plus /></el-icon></el-button>
           <div class="w-px h-4 bg-gray-200 mx-1"></div>
           <el-button circle text size="small" @click="resetZoom" :title="$t('Reset View')"><el-icon><Refresh /></el-icon></el-button>
        </div>
        <!-- Config Bar: horizontal scrollable glass cards -->
        <div v-show="showMobileConfigBar" class="mobile-config-bar shrink-0 border-t border-gray-200/60 bg-gradient-to-r from-gray-50/98 via-white/95 to-gray-50/98 backdrop-blur-xl">
          <div class="flex gap-2.5 overflow-x-auto px-3 py-2.5 scrollbar-hide">
            <!-- Subscribe / Premium -->
            <div class="config-chip bg-gradient-to-br from-amber-400 to-orange-500 !border-0" @click.stop="priceVisible = true">
              <span class="config-chip-label !text-amber-100">VIP</span>
              <span class="text-lg leading-none">👑</span>
            </div>
            <!-- Image Size -->
            <div class="config-chip" @click="openMobileSizePicker">
              <span class="config-chip-label">{{ $t('ImageSize') }}</span>
              <span class="config-chip-value">{{ imageWidth }}×{{ imageHeight }}</span>
            </div>
            <!-- Background Color -->
            <div class="config-chip">
              <span class="config-chip-label">{{ $t('Background Color') }}</span>
              <el-color-picker
                :model-value="backgroundColor"
                @update:model-value="backgroundColor = $event"
                :predefine="predefineColors"
                size="small"
                show-alpha
              />
            </div>
            <!-- Background Image -->
            <div class="config-chip">
              <span class="config-chip-label">{{ $t('Background Image') }}</span>
              <div class="flex gap-1">
                <div class="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer bg-blue-50 text-blue-500" @click="triggerMobileBgInput">
                  <el-icon :size="14"><Plus /></el-icon>
                </div>
                <div v-if="bgImageBase64" class="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer bg-orange-50 text-orange-500" @click="openMobileBgSettings">
                  <el-icon :size="14"><Setting /></el-icon>
                </div>
              </div>
            </div>
            <!-- Color Palette -->
            <div class="config-chip" @click="openMobilePalettePicker">
              <span class="config-chip-label">{{ $t('Color Palette') }}</span>
              <div class="flex gap-0.5">
                <span v-for="c in (currentPalette && currentPalette.length ? currentPalette.slice(0,5) : ['#4f46e5','#ec4899','#f59e0b','#10b981','#3b82f6'])" :key="c" class="w-3 h-3 rounded-full border border-white/60 shadow-sm" :style="{ background: c }"></span>
              </div>
            </div>
            <!-- Font -->
            <div class="config-chip" @click="openMobileFontPicker">
              <span class="config-chip-label">{{ $t('Font') }}</span>
              <span class="config-chip-value text-xs truncate max-w-[56px]">{{ selectedFont }}</span>
            </div>
            <!-- Drawing Mask -->
            <div class="config-chip" @click="drawingBoardVisible = true">
              <span class="config-chip-label">{{ $t('Drawing Mask') }}</span>
              <div class="w-7 h-7 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
                <el-icon :size="15"><EditPen /></el-icon>
              </div>
            </div>
            <!-- Mask Gallery -->
            <div class="config-chip" @click="maskVisible = true">
              <span class="config-chip-label">{{ $t('Shape Mask') }}</span>
              <div class="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-50 text-amber-500">
                <el-icon :size="15"><StarFilled /></el-icon>
              </div>
            </div>
            <!-- Custom Mask -->
            <div class="config-chip" @click="triggerMobileMaskInput">
              <span class="config-chip-label">{{ $t('Mask') }}</span>
              <div class="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-50 text-blue-500 transition-colors">
                <el-icon :size="14"><UploadFilled /></el-icon>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Bar -->
        <div class="mobile-action-bar shrink-0 border-t border-gray-200/60 bg-white/95 backdrop-blur-xl safe-area-bottom">
          <div class="flex items-center justify-around px-1 py-1.5">
            <!-- Transform -->
            <button class="action-btn" @click="handleUpdateChart">
              <div class="action-icon-ring bg-green-500">
                <el-icon :size="20"><Refresh /></el-icon>
              </div>
              <span class="action-label">{{ $t('Transform') }}</span>
            </button>
            <!-- Data Editor -->
            <button class="action-btn" @click="showMobileDataEditor = true">
              <div class="action-icon-ring bg-amber-500">
                <el-icon :size="20"><EditPen /></el-icon>
              </div>
              <span class="action-label">{{ $t('Data') }}</span>
            </button>
            <!-- Video/GIF -->
            <button class="action-btn" @click="isRecording ? null : handleExportVideo()">
              <div class="action-icon-ring" :class="isRecording ? 'bg-red-500' : 'bg-purple-500'">
                <el-icon :size="20"><VideoCamera /></el-icon>
              </div>
              <span class="action-label">{{ isRecording ? '...' : $t('Video') }}</span>
            </button>
            <!-- Save -->
            <button class="action-btn" @click="handleMobileSave">
              <div class="action-icon-ring bg-orange-500">
                <el-icon :size="20"><Download /></el-icon>
              </div>
              <span class="action-label">{{ $t('Save') }}</span>
            </button>
          </div>
        </div>

        <!-- Mobile Data Editor (Fullscreen, Tabbed) -->
        <el-dialog v-model="showMobileDataEditor" fullscreen append-to-body destroy-on-close :show-close="false">
          <div class="flex flex-col h-full bg-gray-50">
            <div class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shrink-0 safe-area-top">
              <span class="font-semibold text-base text-gray-800">{{ $t('Data Editor') }}</span>
              <el-button circle text @click="showMobileDataEditor = false">
                <el-icon :size="20"><Close /></el-icon>
              </el-button>
            </div>
            <!-- Tabs -->
            <div class="flex bg-white border-b border-gray-100 px-4 pt-1 shrink-0">
              <button
                class="py-2.5 px-4 text-sm font-medium border-b-2 transition-colors"
                :class="mobileEditorTab === 'ai' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'"
                @click="mobileEditorTab = 'ai'"
              >{{ $t('AI Analysis') }}</button>
              <button
                class="py-2.5 px-4 text-sm font-medium border-b-2 transition-colors"
                :class="mobileEditorTab === 'list' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500'"
                @click="mobileEditorTab = 'list'"
              >{{ $t('Word List') }}</button>
            </div>
            <!-- Tab Content -->
            <div class="flex-1 min-h-0 flex flex-col">
              <!-- AI Analysis Tab -->
              <div v-show="mobileEditorTab === 'ai'" class="flex flex-col h-full p-4">
                <div class="flex items-center gap-2 mb-2">
                  <el-button size="small" plain @click="pasteFromClipboard">
                    <el-icon :size="14"><CopyDocument /></el-icon>
                    {{ $t('Paste') }}
                  </el-button>
                  <el-button v-if="textToAnalyze" size="small" plain type="danger" @click="textToAnalyze = ''">
                    <el-icon :size="14"><Delete /></el-icon>
                    {{ $t('Clear') }}
                  </el-button>
                </div>
                <el-input
                  :model-value="textToAnalyze"
                  :placeholder="$t('Paste text to analyze...')"
                  type="textarea"
                  :rows="10"
                  resize="none"
                  class="flex-1 mb-3"
                  @input="textToAnalyze = $event"
                />
                <el-button
                  type="primary"
                  class="w-full shrink-0"
                  :disabled="!textToAnalyze.trim()"
                  :loading="loading"
                  @click="analyzeText(); showMobileDataEditor = false"
                >{{ $t('AnalyzeGenerate') }}</el-button>
              </div>
              <!-- Word List Tab -->
              <div v-show="mobileEditorTab === 'list'" class="flex flex-col h-full min-h-0">
                <div class="px-4 py-3 shrink-0">
                  <div class="flex gap-2">
                    <el-input v-model="mobileWordKey" :placeholder="$t('Word')" size="small" />
                    <el-input v-model="mobileWordVal" type="number" :placeholder="$t('Val')" size="small" style="width:80px" />
                    <el-button type="primary" size="small" @click="addMobileWord">{{ $t('Add') }}</el-button>
                  </div>
                </div>
                <div class="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
                  <div v-if="processedChartData.length === 0" class="text-center text-gray-400 py-12">
                    <el-icon :size="40"><FolderOpened /></el-icon>
                    <p class="mt-2 text-sm">{{ $t('NoDataAvailable') }}</p>
                  </div>
                  <div v-else class="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      v-for="(row, idx) in processedChartData"
                      :key="idx"
                      class="flex items-center gap-2 px-3 py-2 border-b border-gray-100 last:border-0 bg-white"
                    >
                      <el-input
                        :model-value="row.name"
                        size="small"
                        class="flex-1"
                        @update:model-value="updateData({ index: idx, field: 'name', value: $event })"
                      />
                      <el-input-number
                        :model-value="row.value"
                        :min="1"
                        size="small"
                        style="width:80px"
                        controls-position="right"
                        @update:model-value="updateData({ index: idx, field: 'value', value: $event })"
                      />
                      <el-button link type="danger" size="small" @click="deleteData(idx)">
                        <el-icon><Delete /></el-icon>
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-dialog>

        <!-- Mobile Size Picker -->
        <el-dialog v-model="showMobileSizeDialog" :title="$t('ImageSize')" width="90%" append-to-body destroy-on-close>
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap gap-2">
              <el-button
                v-for="sz in defaultSizes"
                :key="sz.tag"
                :type="imageWidth === sz.width && imageHeight === sz.height ? 'primary' : ''"
                size="small"
                @click="updateImageSize(sz); showMobileSizeDialog = false"
              >{{ sz.tag }}</el-button>
            </div>
            <div class="border-t border-gray-100 pt-3 flex items-center gap-3">
              <span class="text-sm text-gray-500">W</span>
              <el-input-number v-model="imageWidth" :min="100" :max="4096" size="small" controls-position="right" class="flex-1" />
              <span class="text-sm text-gray-500">H</span>
              <el-input-number v-model="imageHeight" :min="100" :max="4096" size="small" controls-position="right" class="flex-1" />
              <el-button size="small" type="primary" @click="showMobileSizeDialog = false">{{ $t('OK') }}</el-button>
            </div>
          </div>
        </el-dialog>

        <!-- Mobile Background Image Settings -->
        <el-dialog v-if="bgImageBase64" v-model="showMobileBgSettings" :title="$t('Background Image')" width="88%" append-to-body destroy-on-close :close-on-click-modal="false">
          <div class="flex flex-col gap-4">
            <!-- Preview -->
            <div class="w-full h-32 rounded-lg border border-gray-200 relative overflow-hidden" :style="{ backgroundColor }">
              <div
                class="absolute inset-0"
                :style="{
                  backgroundImage: `url(${bgImageBase64})`,
                  backgroundSize: mobileBgFitMode === 'stretch' ? '100% 100%' : mobileBgFitMode,
                  backgroundRepeat: mobileBgFitMode === 'repeat' ? 'repeat' : 'no-repeat',
                  opacity: mobileBgOpacity,
                  filter: mobileBgBlur > 0 ? `blur(${mobileBgBlur}px)` : 'none'
                }"
              ></div>
            </div>
            <!-- Opacity -->
            <div>
              <div class="flex justify-between text-sm mb-1">
                <span class="text-gray-600">{{ $t('BgOpacity') }}</span>
                <span class="text-gray-400">{{ Math.round(mobileBgOpacity * 100) }}%</span>
              </div>
              <el-slider v-model="mobileBgOpacity" :min="0" :max="1" :step="0.05" />
            </div>
            <!-- Blur -->
            <div>
              <div class="flex justify-between text-sm mb-1">
                <span class="text-gray-600">{{ $t('BgBlur') }}</span>
                <span class="text-gray-400">{{ mobileBgBlur }}px</span>
              </div>
              <el-slider v-model="mobileBgBlur" :min="0" :max="30" :step="1" />
            </div>
            <!-- Fit Mode -->
            <div>
              <span class="text-sm text-gray-600 block mb-2">{{ $t('BgFitMode') }}</span>
              <div class="flex gap-2">
                <el-button
                  v-for="m in bgFitOptions"
                  :key="m.value"
                  :type="mobileBgFitMode === m.value ? 'primary' : ''"
                  size="small"
                  @click="mobileBgFitMode = m.value"
                >{{ $t(m.label) }}</el-button>
              </div>
            </div>
            <!-- Actions -->
            <div class="flex gap-2 pt-2 border-t border-gray-100">
              <el-button type="danger" plain size="small" @click="clearBgImage(); showMobileBgSettings = false">
                {{ $t('Remove') }}
              </el-button>
              <div class="flex-1"></div>
              <el-button size="small" @click="showMobileBgSettings = false">{{ $t('Cancel') }}</el-button>
              <el-button type="primary" size="small" @click="applyMobileBgSettings(); showMobileBgSettings = false">{{ $t('Apply') }}</el-button>
            </div>
          </div>
        </el-dialog>

        <!-- Mobile Palette Picker -->
        <el-dialog v-model="showMobilePaletteDialog" :title="$t('Color Palette')" width="90%" append-to-body destroy-on-close>
          <div class="grid gap-2" style="grid-template-columns: repeat(2, 1fr);">
            <div
              v-for="p in COLOR_PALETTES"
              :key="p.name"
              class="flex items-center gap-2 p-2.5 rounded-lg cursor-pointer border transition-all active:scale-95"
              :class="selectedPalette === p.name ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 bg-white'"
              @click="selectMobilePalette(p.name)"
            >
              <div v-if="p.colors" class="flex gap-0.5 shrink-0">
                <span v-for="c in p.colors.slice(0,4)" :key="c" class="w-4 h-4 rounded-full border border-white/60" :style="{ background: c }"></span>
              </div>
              <div v-else class="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 via-pink-400 to-amber-400 flex items-center justify-center shrink-0">
                <el-icon :size="12" color="#fff"><MagicStick /></el-icon>
              </div>
              <span class="text-xs font-medium text-gray-700 truncate">{{ p.label }}</span>
            </div>
          </div>
        </el-dialog>

        <!-- Mobile Font Picker -->
        <el-dialog v-model="showMobileFontDialog" :title="$t('SelectFont')" width="90%" append-to-body destroy-on-close>
          <div class="flex flex-col gap-2">
            <el-input
              v-model="mobileFontSearch"
              :placeholder="$t('SearchFonts')"
              size="small"
              clearable
            >
              <template #prefix>
                <el-icon :size="16"><Search /></el-icon>
              </template>
            </el-input>
            <div class="flex flex-col gap-1 max-h-[55vh] overflow-y-auto -mx-2">
              <div
                v-for="font in filteredMobileFonts"
                :key="font.value"
                class="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition-colors"
                :class="{ 'bg-indigo-50 text-indigo-700 font-semibold': selectedFont === font.value }"
                @click="selectMobileFont(font.value)"
              >
                <span class="text-base" :style="{ fontFamily: font.value }">{{ font.label }}</span>
                <span v-if="selectedFont === font.value" class="ml-auto text-indigo-500 text-lg">✓</span>
              </div>
              <div v-if="filteredMobileFonts.length === 0" class="text-center text-gray-400 py-8 text-sm">
                {{ $t('NoFontsFound') }}
              </div>
            </div>
          </div>
        </el-dialog>
      </template>

    <!-- Mask Gallery Dialog -->
    <el-dialog v-model="maskVisible" :title="$t('Select Shape')" :width="dialogWidth" append-to-body destroy-on-close>
       <div v-if="!isSubscribed" class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 mb-4 flex items-center gap-2 border border-indigo-100">
          <el-icon class="text-indigo-500"><PriceTag /></el-icon>
          <span class="text-sm text-indigo-700 flex-1">{{ $t('Browse free — subscribe to use any shape in your word cloud.') }}</span>
          <el-button type="primary" size="small" @click="priceVisible = true; maskVisible = false">{{ $t('View Plans') }}</el-button>
       </div>
       <SelectMask
         :inDialog="true"
         @mask-selected="handleMaskSelected"
         @show-pricing="priceVisible = true"
       />
    </el-dialog>

    <!-- Drawing Board Dialog -->
    <el-dialog v-model="drawingBoardVisible" :title="$t('Drawing Mask')" :width="dialogWidth" append-to-body destroy-on-close>
      <DrawingBoard
        @confirm="handleDrawingConfirm"
        @cancel="drawingBoardVisible = false"
      />
    </el-dialog>

    <!-- Pricing Dialog (desktop + mobile unified) -->
    <el-dialog
      v-model="priceVisible"
      :fullscreen="isMobile"
      :show-close="false"
      :width="isMobile ? undefined : priceDialogWidth"
      append-to-body
      :destroy-on-close="isMobile"
      :class="{ 'clean-dialog': !isMobile }"
    >
      <!-- Desktop -->
      <template v-if="!isMobile">
        <div class="relative">
          <el-button class="absolute -top-2 right-0 z-20" text circle @click="dismissPricing">
            <el-icon :size="20"><Close /></el-icon>
          </el-button>
          <el-tabs class="custom-tabs">
            <el-tab-pane :label="$t('Plans')"><PricePage /></el-tab-pane>
            <el-tab-pane :label="$t('My Account')"><Account @subscription-updated="checkSubscription" /></el-tab-pane>
          </el-tabs>
        </div>
      </template>
      <!-- Mobile -->
      <template v-else>
        <div class="flex flex-col h-full bg-gray-50">
          <div class="flex items-center justify-end px-4 py-2 shrink-0 safe-area-top">
            <el-button circle text @click="dismissPricing">
              <el-icon :size="22"><Close /></el-icon>
            </el-button>
          </div>
          <div class="flex-1 overflow-auto">
            <MobilePricing @done="onMobilePricingDone" />
          </div>
        </div>
      </template>
    </el-dialog>

    <el-dialog v-model="serviceVisible" :title="$t('Customer Service')" :width="serviceDialogWidth" append-to-body>
      <div class="service-dialog-scroll">
        <Service />
      </div>
    </el-dialog>

    <!-- Onboarding Tour (desktop only) -->
    <OnboardingTour
      v-if="!isMobile"
      :visible="showOnboarding"
      @close="closeOnboarding"
    />

  </div>
</template>

<script>
import {
  ElButton, ElIcon, ElDialog, ElTabs, ElTabPane, ElNotification
} from 'element-plus';
/* eslint-disable no-unused-vars */
import {
  Menu, Setting, Remove, Plus, Refresh, PriceTag, Close, QuestionFilled,
  EditPen, StarFilled, UploadFilled, VideoCamera, Download,
  Delete, FolderOpened, MagicStick, Search, CopyDocument, Headset
} from '@element-plus/icons-vue';
/* eslint-enable no-unused-vars */
import { ref } from 'vue';

import LeftSidebar from './home/LeftSidebar.vue';
import RightSidebar from './home/RightSidebar.vue';
import WordCloudChart from './home/WordCloudChart.vue';
import DrawingBoard from './home/DrawingBoard.vue';
import OnboardingTour from './home/OnboardingTour.vue';
import SelectMask from './SelectMask.vue';
import PricePage from './PricePage.vue';
import Service from './Service.vue';
import Account from './Account.vue';
import MobilePricing from './MobilePricing.vue';

import dogImg from '../assets/love.png';
import { CONFIG_GOOGLE_FONTS } from '@/config';
import { getPalette, COLOR_PALETTES } from '@/utils/colorPalettes';
import { useSEO } from '@/composables/useSEO';

export default {
  name: 'HomePage',
  components: {
    ElButton, ElIcon, ElDialog, ElTabs, ElTabPane,
    Menu, Setting, Remove, Plus, Refresh, PriceTag, Close, QuestionFilled, EditPen, StarFilled, UploadFilled, VideoCamera, Download, Delete, FolderOpened, MagicStick, Search, CopyDocument, Headset,
    LeftSidebar, RightSidebar, WordCloudChart, DrawingBoard, OnboardingTour, SelectMask, PricePage, Service, Account, MobilePricing
  },
  setup() {
    return {
      predefineColors: ref(['#4f46e5', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#111827', '#ffffff'])
    };
  },
  data() {
    return {
      COLOR_PALETTES,
      // Core Data
      chartData: [],
      textToAnalyze: '',
      loading: false,

      // Configuration
      backgroundColor: '#FFFFFF',
      imageWidth: 500,
      imageHeight: 500,
      minSize: 10,
      maxSize: 180,
      selectedFont: 'Roboto',
      fontOptions: CONFIG_GOOGLE_FONTS,
      selectedSize: null,
      defaultSizes: [
        { width: 800, height: 600, tag: "800x600" },
        { width: 1024, height: 768, tag: "1024x768" },
        { width: 1920, height: 1080, tag: "1080p" },
        { width: 500, height: 500, tag: "Square (S)" },
        { width: 1000, height: 1000, tag: "Square (M)" },
      ],
      imageUrls: ['cat.png', 'flight.png', 'flower.png'],

      // Masks
      selectedMask: null,
      customMaskDataUrl: null,

      // Background Image
      bgImageBase64: '',
      bgOpacity: 0.3,
      bgBlur: 0,
      bgFitMode: 'cover',

      // Color Palette
      selectedPalette: 'random',

      // Drawing Board
      drawingBoardVisible: false,

      // Video/GIF
      isRecording: false,
      isGeneratingGif: false,

      // Onboarding
      showOnboarding: false,

      // UI State
      isMobile: false,
      showDataPanel: true,
      showOptionsPanel: true,

      // Mobile iOS-style Layout
      showMobileDataEditor: false,
      showMobileConfigBar: true,
      showMobileFontDialog: false,
      mobileFontSearch: '',
      showMobileSizeDialog: false,
      showMobileBgSettings: false,
      mobileBgOpacity: 0.3,
      mobileBgBlur: 0,
      mobileBgFitMode: 'cover',
      bgFitOptions: [
        { value: 'cover', label: 'BgCover' },
        { value: 'contain', label: 'BgContain' },
        { value: 'repeat', label: 'BgTile' },
        { value: 'stretch', label: 'BgStretch' }
      ],
      showMobilePaletteDialog: false,
      mobilePaletteIndex: 0,
      mobileEditorTab: 'ai',
      mobileWordKey: '',
      mobileWordVal: '',

      zoomLevel: 1,
      isSubscribed: false,

      // Dialogs
      maskVisible: false,
      priceVisible: false,
      serviceVisible: false,
    };
  },
  computed: {
    processedChartData() { return this.chartData.slice(); },
    currentMaskSrc() { return this.customMaskDataUrl || this.selectedMask || dogImg; },
    currentPalette() { return getPalette(this.selectedPalette); },
    filteredMobileFonts() {
      const q = (this.mobileFontSearch || '').toLowerCase();
      if (!q) return this.fontOptions;
      return this.fontOptions.filter(f => f.label.toLowerCase().includes(q));
    },
    artboardStyle() {
      if (this.isMobile) {
        const maxW = window.innerWidth - 32;
        const fitScale = Math.min(1, maxW / this.imageWidth);
        return {
          width: `${this.imageWidth}px`,
          height: `${this.imageHeight}px`,
          maxWidth: '100%',
          transform: `scale(${fitScale * this.zoomLevel})`,
          transformOrigin: 'center center'
        };
      }
      return {
        width: `${this.imageWidth}px`,
        height: `${this.imageHeight}px`,
        transform: `scale(${this.zoomLevel})`,
        transformOrigin: 'center center'
      };
    },
    dialogWidth() { return this.isMobile ? '95%' : '600px'; },
    priceDialogWidth() { return this.isMobile ? '95%' : '750px'; },
    serviceDialogWidth() { return this.isMobile ? '95%' : '400px'; }
  },
  created() {
    this.checkMobile();
    if (this.isMobile) {
      this.showDataPanel = false;
      this.showOptionsPanel = false;
    }
    window.addEventListener('resize', this.checkMobile);
    this.$watch('selectedFont', (f) => this.loadSelectedFont(f));
  },
  mounted() {
    this.$i18n.locale = navigator.language;
    useSEO({
      title: 'WordCloudStudio — Free Online Word Cloud Generator',
      description: 'Create stunning word clouds online for free. Custom fonts, shapes, colors, and high-res exports. No download required — start creating your word art now!',
      keywords: 'word cloud generator, word art creator, tag cloud maker, text visualization, wordcloud studio, free word cloud, online word cloud',
      canonical: '/',
      ogTitle: 'WordCloudStudio — Free Online Word Cloud Generator',
      ogDescription: 'Create stunning word clouds online for free. Custom fonts, shapes, colors, and high-res exports. Start creating your word art now!',
      ogImage: 'https://wordcloudstudio.com/wcdw.png',
      twitterTitle: 'WordCloudStudio — Free Online Word Cloud Generator',
      twitterDescription: 'Create stunning word clouds online for free. Custom fonts, shapes, colors, and high-res exports.',
      twitterImage: 'https://wordcloudstudio.com/wcdw.png'
    });
    this.generateData();
    this.checkSubscription();
    this.loadSelectedFont(this.selectedFont);

    const pricingDismissed = localStorage.getItem('pricingDismissed');
    if (!this.isSubscribed && !pricingDismissed) {
      setTimeout(() => {
        this.priceVisible = true;
      }, 1000);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 1024;
    },
    toggleDataPanel() {
        if (this.isMobile) {
            this.showMobileDataEditor = true;
        } else {
            this.showDataPanel = !this.showDataPanel;
        }
    },
    toggleOptionsPanel() {
        if (this.isMobile) {
            this.showMobileConfigBar = !this.showMobileConfigBar;
        } else {
            this.showOptionsPanel = !this.showOptionsPanel;
        }
    },
    // Mobile iOS-style Config Bar Methods
    openMobileSizePicker() {
      this.showMobileSizeDialog = true;
    },
    openMobileFontPicker() {
      this.mobileFontSearch = '';
      this.showMobileFontDialog = true;
    },
    selectMobileFont(val) {
      this.selectedFont = val;
      this.showMobileFontDialog = false;
    },
    openMobileBgSettings() {
      this.mobileBgOpacity = this.bgOpacity;
      this.mobileBgBlur = this.bgBlur;
      this.mobileBgFitMode = this.bgFitMode;
      this.showMobileBgSettings = true;
    },
    applyMobileBgSettings() {
      this.bgOpacity = this.mobileBgOpacity;
      this.bgBlur = this.mobileBgBlur;
      this.bgFitMode = this.mobileBgFitMode;
    },
    openMobilePalettePicker() {
      this.showMobilePaletteDialog = true;
    },
    selectMobilePalette(name) {
      this.showMobilePaletteDialog = false;
      this.$nextTick(() => {
        this.selectedPalette = name;
      });
    },
    triggerMobileBgInput() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) this.handleBgFileSelect(file);
      };
      input.click();
    },
    triggerMobileMaskInput() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) this.handleMaskFileSelect(file);
      };
      input.click();
    },
    async pasteFromClipboard() {
      try {
        const text = await navigator.clipboard.readText();
        if (text) this.textToAnalyze = text;
      } catch {
        this.$message.warning(this.$t('ClipboardDenied') || 'Cannot access clipboard');
      }
    },
    addMobileWord() {
      const key = this.mobileWordKey.trim();
      const val = parseInt(this.mobileWordVal);
      if (!key || isNaN(val) || val < 1) return;
      this.addData({ key, value: val });
      this.mobileWordKey = '';
      this.mobileWordVal = '';
    },
    async handleMobileShare() {
      try {
        if (!this.$refs.wordCloud) return;
        const dataUrl = await this.$refs.wordCloud.getChartDataUrl?.();
        if (!dataUrl) { this.$refs.wordCloud.exportChart(); return; }
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'wordcloud.png', { type: 'image/png' });
        if (navigator.share) {
          await navigator.share({ files: [file], title: 'WordCloudStudio' });
        } else {
          this.$refs.wordCloud.exportChart();
        }
      } catch (e) {
        if (e?.name !== 'AbortError') {
          this.$refs.wordCloud?.exportChart();
        }
      }
    },
    handleMobileSave() {
      if (this.$refs.wordCloud) {
        this.$refs.wordCloud.exportChart();
      }
    },

    // Zoom
    zoomIn() { this.zoomLevel = Math.min(this.zoomLevel * 1.1, 3); },
    zoomOut() { this.zoomLevel = Math.max(this.zoomLevel / 1.1, 0.1); },
    resetZoom() { this.zoomLevel = 1; },

    // Logic Wrappers
    generateData() {
      this.chartData = [
         { name: "🌼", value: 1800 }, { name: "Design", value: 1200 },
         { name: "Creative", value: 1100 }, { name: "Studio", value: 1000 },
         { name: "Visualization", value: 950 }, { name: "Art", value: 900 },
         { name: "Code", value: 850 }, { name: "Future", value: 800 },
         { name: "Innovation", value: 780 }, { name: "Data", value: 750 },
         { name: "Cloud", value: 720 }, { name: "Tech", value: 700 },
         { name: "Growth", value: 680 }, { name: "Vision", value: 650 },
         { name: "Smart", value: 620 }, { name: "AI", value: 600 },
         { name: "Web", value: 580 }, { name: "Mobile", value: 560 },
         { name: "App", value: 540 }, { name: "UI", value: 520 },
         { name: "UX", value: 500 }, { name: "Global", value: 480 },
         { name: "Fast", value: 460 }, { name: "Secure", value: 440 },
         { name: "Style", value: 420 }, { name: "Modern", value: 400 },
         { name: "Clean", value: 380 }, { name: "Power", value: 360 },
         { name: "Energy", value: 350 }, { name: "Concept", value: 340 },
         { name: "Idea", value: 330 }, { name: "Inspire", value: 320 },
         { name: "Dream", value: 310 }, { name: "Create", value: 300 },
         { name: "Build", value: 290 }, { name: "Launch", value: 280 },
         { name: "Scale", value: 270 }, { name: "Impact", value: 260 },
         { name: "Value", value: 250 }, { name: "Focus", value: 240 },
         { name: "Team", value: 230 }, { name: "Work", value: 220 },
         { name: "Life", value: 210 }, { name: "Color", value: 200 },
         { name: "Shape", value: 195 }, { name: "Form", value: 190 },
         { name: "Line", value: 185 }, { name: "Space", value: 180 },
         { name: "Texture", value: 175 }, { name: "Pattern", value: 170 },
         { name: "Balance", value: 165 }, { name: "Harmony", value: 160 },
         { name: "Rhythm", value: 155 }, { name: "Contrast", value: 150 },
         { name: "Light", value: 145 }, { name: "Shadow", value: 140 },
         { name: "Vibrant", value: 135 }, { name: "Bold", value: 130 },
         { name: "Dynamic", value: 125 }, { name: "Fluid", value: 120 },
         { name: "Organic", value: 115 }, { name: "Geometry", value: 110 },
         { name: "Digital", value: 105 }, { name: "Analog", value: 100 },
         { name: "Pixel", value: 95 }, { name: "Vector", value: 90 },
         { name: "Raster", value: 85 }, { name: "Canvas", value: 80 },
         { name: "Brush", value: 75 }, { name: "Ink", value: 70 },
         { name: "Paper", value: 65 }, { name: "Print", value: 60 },
         { name: "Screen", value: 55 }, { name: "Touch", value: 50 },
         { name: "Mouse", value: 45 }, { name: "Keyboard", value: 40 },
         { name: "Logic", value: 35 }, { name: "System", value: 30 },
         { name: "Network", value: 25 }, { name: "Connect", value: 24 },
         { name: "Share", value: 23 }, { name: "Like", value: 22 },
         { name: "Follow", value: 21 }, { name: "Trend", value: 20 },
         { name: "Viral", value: 19 }, { name: "Social", value: 18 },
         { name: "Media", value: 17 }, { name: "Content", value: 16 },
         { name: "Story", value: 15 }, { name: "Brand", value: 14 },
         { name: "Identity", value: 13 }, { name: "Logo", value: 12 },
         { name: "Icon", value: 11 }, { name: "Image", value: 10 },
         { name: "Photo", value: 9 }, { name: "Photo", value: 8 },
         { name: "Video", value: 7 }, { name: "Audio", value: 6 },
         { name: "Music", value: 5 }, { name: "Sound", value: 4 },
         { name: "Noise", value: 3 }, { name: "Silence", value: 2 },
         { name: "Peace", value: 1 }
      ];
    },
    addData(item) { this.chartData.push({ name: item.key, value: Number(item.value) }); },
    deleteData(idx) { this.chartData.splice(idx, 1); },
    updateData({ index, field, value }) {
      if (this.chartData[index]) {
        if (field === 'name') this.chartData[index].name = value;
        else if (field === 'value') this.chartData[index].value = Number(value);
      }
    },
    analyzeText() {
       if (!this.checkSubscription()) return;
       const words = this.textToAnalyze.split(/\s+/);
       const map = {};
       words.forEach(w => w && (map[w] = (map[w] || 0) + 1));
       this.chartData = Object.entries(map).map(([k,v]) => ({ name: k, value: v }));
    },
    checkSubscription() {
       const status = localStorage.getItem("purchaseStatus");
       this.isSubscribed = (status === "purchased");
       return this.isSubscribed;
    },

    // Pass-throughs
    handleUpdateChart() { this.$refs.wordCloud?.updateChart(); },
    updateImageSize(s) { this.imageWidth = s.width; this.imageHeight = s.height; },
    handleMaskSelected(url) {
        if (url && (url.startsWith('data:') || url.startsWith('http') || url.startsWith('blob:'))) {
           this.selectedMask = url;
        } else {
           try {
              this.selectedMask = require(`@/assets/${url}`);
           } catch (e) {
              console.warn("Could not load local mask:", url);
              this.selectedMask = url;
           }
        }
        this.maskVisible = false;
    },
    handleMaskFileSelect(file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            this.customMaskDataUrl = e.target.result;
            this.selectedMask = null;
        };
        reader.readAsDataURL(file);
    },

    // Background Image
    handleBgFileSelect(file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            this.bgImageBase64 = e.target.result;
        };
        reader.readAsDataURL(file);
    },
    clearBgImage() {
        this.bgImageBase64 = '';
        this.bgOpacity = 0.3;
        this.bgBlur = 0;
        this.bgFitMode = 'cover';
    },

    // Drawing Board
    handleDrawingConfirm(base64DataUrl) {
        this.customMaskDataUrl = base64DataUrl;
        this.drawingBoardVisible = false;
    },

    // Video Export
    async handleExportVideo() {
        if (!this.$refs.wordCloud) return;
        this.isRecording = true;

        try {
            const videoBlob = await this.$refs.wordCloud.recordVideo(4000, 10);
            const date = new Date();
            const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            this.$refs.wordCloud.downloadBlob(videoBlob, `wordcloudstudio_${dateString}_${Date.now()}.mp4`);
            ElNotification({
                title: this.$t('Success'),
                message: this.$t('Video exported successfully!'),
                type: 'success'
            });
        } catch (err) {
            console.error('Video export failed:', err);
            ElNotification({
                title: this.$t('Export Failed'),
                message: err.message || this.$t('Could not export video'),
                type: 'error'
            });
        } finally {
            this.isRecording = false;
        }
    },

    // GIF Export
    async handleExportGif() {
        if (!this.$refs.wordCloud) return;
        this.isGeneratingGif = true;

        try {
            const gifBlob = await this.$refs.wordCloud.exportGif(20, 2500);
            const date = new Date();
            const dateString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            this.$refs.wordCloud.downloadBlob(gifBlob, `wordcloudstudio_${dateString}_${Date.now()}.gif`);
            ElNotification({
                title: this.$t('Success'),
                message: this.$t('GIF exported successfully!'),
                type: 'success'
            });
        } catch (err) {
            console.error('GIF export failed:', err);
            ElNotification({
                title: this.$t('Export Failed'),
                message: err.message || this.$t('Could not export GIF'),
                type: 'error'
            });
        } finally {
            this.isGeneratingGif = false;
        }
    },

    async exportLayout() {
        if (!this.checkSubscription()) { this.priceVisible = true; return; }
        const oldZoom = this.zoomLevel;
        this.zoomLevel = 1;
        this.$nextTick(async () => {
            await this.$refs.wordCloud?.exportChart();
            this.zoomLevel = oldZoom;
        });
    },
    loadSelectedFont(font) {
        const link = document.createElement('link');
        link.href = `//fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:wght@400;700&display=swap`;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    },
    changeLang(l) { this.$i18n.locale = l; },
    onMobilePricingDone() {
      this.checkSubscription();
      this.priceVisible = false;
    },
    dismissPricing() {
      localStorage.setItem('pricingDismissed', 'true');
      this.priceVisible = false;
      // Show onboarding tour for non-subscribed first-time users
      if (!this.isMobile && !this.isSubscribed && !localStorage.getItem('onboardingDone')) {
        this.$nextTick(() => {
          this.showOnboarding = true;
        });
      }
    },
    closeOnboarding() {
      this.showOnboarding = false;
      localStorage.setItem('onboardingDone', 'true');
    }
  }
}
</script>

<style scoped>
/* Transitions for Panels — delegated to glass-theme.css (.panel-slide-*) */

/* ═══════════════════════════════════════════════════════
   Mobile iOS-style Config Bar
   ═══════════════════════════════════════════════════════ */
.mobile-config-bar {
  -webkit-overflow-scrolling: touch;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Glass-morphism Config Chip */
.config-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 68px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}
.config-chip:active {
  transform: scale(0.95);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.config-chip-label {
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}
.config-chip-value {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  line-height: 1;
}

/* ═══════════════════════════════════════════════════════
   Mobile Action Bar
   ═══════════════════════════════════════════════════════ */
.mobile-action-bar {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  background: none;
  border: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.15s ease;
}
.action-btn:active {
  opacity: 0.7;
}
.action-icon-ring {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease;
}
.action-btn:active .action-icon-ring {
  transform: scale(0.92);
}
.action-label {
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1;
}

/* Safe area insets for notched phones */
.safe-area-top { padding-top: env(safe-area-inset-top, 0px); }
.safe-area-bottom { padding-bottom: env(safe-area-inset-bottom, 0px); }

/* Service dialog scroll */
.service-dialog-scroll {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 4px;
}
</style>
<style>
/* Panel transitions delegated to glass-theme.css (.panel-slide-*) */
</style>
